import { useCallback, useEffect, useState } from 'react';

import type { AnimationState } from './modal-utils';

/** Options accepted by {@link useModalPresence}. */
interface UseModalPresenceOptions {
  /** Whether the modal's DOM tree should be kept in the document after closing. */
  keepMounted: boolean;
  /** Whether the modal is currently open. */
  open: boolean;
}

/** Return value of {@link useModalPresence}. */
interface UseModalPresenceResult {
  /** Current animation phase of the modal. */
  animationState: AnimationState;
  /** Callback to advance the animation state; also drives the mount/unmount lifecycle. */
  handleAnimationStateChange: (state: AnimationState) => void;
  /** Whether the modal's subtree should be rendered into the DOM. */
  isMounted: boolean;
}

/**
 * Manages the mounted/unmounted lifecycle and animation state of a modal.
 *
 * The modal is kept in the DOM while it is open or while `keepMounted` is
 * `true`. Once the exit animation completes (i.e. `animationState` reaches
 * `"exited"`) and `keepMounted` is `false`, the modal is unmounted.
 *
 * @param options - Configuration for keep-mounted behaviour and open state.
 * @param options.keepMounted - Whether the modal DOM tree should be retained after closing.
 * @param options.open - Whether the modal is currently open.
 * @returns The current animation state, a state-change handler, and a flag
 *   indicating whether the modal subtree should be mounted.
 */
export const useModalPresence = ({ keepMounted, open }: UseModalPresenceOptions): UseModalPresenceResult => {
  const [animationState, setAnimationState] = useState<AnimationState>(() => (open ? 'entering' : 'exited'));
  const [isMounted, setIsMounted] = useState(() => open || keepMounted);

  const handleAnimationStateChange = useCallback(
    (state: AnimationState): void => {
      setAnimationState(state);

      if (state === 'exited' && !open && !keepMounted) {
        setIsMounted(false);
      }
    },
    [keepMounted, open],
  );

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect -- enter state must be visible on the first remount frame
      setAnimationState('entering');
    }

    if (open || keepMounted) {
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect -- reopen needs to remount immediately
      setIsMounted(true);
    }
  }, [keepMounted, open]);

  return {
    animationState,
    handleAnimationStateChange,
    isMounted,
  };
};
